import { defineStore } from 'pinia';
import { fetchWrapper } from '@/utils/helpers/fetch-wrapper';
import { useAuthStore } from '@/stores/auth';

export interface PsychologistFilters {
  specializations: string[];
  mentalStates: string[];
}

export interface UserRuntimeContext {
  type: string;
  companyId: string | null;
  psychoCompanyId: string | null;
  activeFrom: string | null;
}

export interface BaseProfile {
  fullName: string;
  birthDate: string;
  sex: string;
  email: string;
  relationshipStatus: string;
  kids: number;
  techAffinity: number;
  identifiesAs: string;
  joinedAt: string;
  role: string;
  imageUrl: string;
  id: string;
  runtimeContext?: UserRuntimeContext;
}

export interface Education {
  institution: string;
  specialty: string;
  graduationDate: string;
}

export interface PsychologistProfile extends BaseProfile {
  bio: string | null;
  education: Education[];
  focusAreas: string[];
  specializations: string[];
  yearsOfPractice: number;
  hoursOfPractice: number;
}

export interface PatchUserProfileDto {
  fullName?: string;
  sex?: string;
  relationshipStatus?: string;
  kids?: number;
  techAffinity?: number;
  birthDate?: string;
  identifiesAs?: string;
  joinedAt?: string;
  imageUrl?: string;
}

export interface PatchPsychologistProfileDto {
  userProfile?: PatchUserProfileDto;
  bio?: string;
  education?: Education[];
  yearsOfPractice?: number;
  hoursOfPractice?: number;
  specializations?: string[];
  focusAreas?: string[];
}

export type Profile = BaseProfile | PsychologistProfile;

const profileUrl = `${import.meta.env.VITE_API_URL}/v3/profile`;
const psychologistProfileUrl = `${import.meta.env.VITE_API_URL}/v3/psychologist-profile`;

export const useProfileStore = defineStore({
  id: 'profile',
  state: () => ({
    profile: (localStorage.getItem('profile') ? JSON.parse(localStorage.getItem('profile')!) : null) as Profile | null,
    loading: false,
    errorCode: null as string | null,
    name: (localStorage.getItem('fullName') ? JSON.parse(localStorage.getItem('fullName')!) : null) as string | null,
    role: (localStorage.getItem('role') ? JSON.parse(localStorage.getItem('role')!) : null) as string | null,
    psychologistFilters: null as PsychologistFilters | null,
    pendingAvatarPreviewUrl: null as string | null,
    pendingAvatarStatus: null as 'error' | null,
    pendingAvatarStatusMessageKey: null as string | null,
  }),

  getters: {
    isPsychologist: (state): boolean => state.profile?.role === 'psychologist',
    profileData: (state) => state.profile,
    avatarImageUrl: (state): string | null => state.pendingAvatarPreviewUrl || state.profile?.imageUrl || null,
    pendingAvatarErrorMessageKey: (state): string | null =>
      state.pendingAvatarStatus === 'error' ? state.pendingAvatarStatusMessageKey : null,
    isLoading: (state) => state.loading,
    serverError: (state) => state.errorCode,
    getRole: (state): string | null => state.role,
    currentCompanyId: (state): string | null => state.profile?.runtimeContext?.companyId ?? null,
    specializationsOptions: (state): string[] => state.psychologistFilters?.specializations ?? [],
    mentalStatesOptions: (state): string[] => state.psychologistFilters?.mentalStates ?? [],
  },

  actions: {
    async fetchProfile() {
      this.loading = true;
      try {
        const response = await fetchWrapper.get(profileUrl);
        this.profile = response;
        localStorage.setItem('profile', JSON.stringify(response));
        if (response.role) {
          this.role = response.role;
          localStorage.setItem('role', JSON.stringify(response.role));
        }
        return response;
      } catch (error: any) {
        this.errorCode = error?.code ?? 'profile_fetch_error';
        throw { ...error, code: this.errorCode };
      } finally {
        this.loading = false;
      }
    },

    setPendingAvatarPreview(file: File) {
      this.clearPendingAvatarPreview();
      this.clearPendingAvatarStatus();
      this.pendingAvatarPreviewUrl = URL.createObjectURL(file);
    },

    setPendingAvatarStatus(status: 'error', messageKey: string) {
      this.pendingAvatarStatus = status;
      this.pendingAvatarStatusMessageKey = messageKey;
    },

    clearPendingAvatarStatus() {
      this.pendingAvatarStatus = null;
      this.pendingAvatarStatusMessageKey = null;
    },

    clearPendingAvatarPreview() {
      if (this.pendingAvatarPreviewUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(this.pendingAvatarPreviewUrl);
      }

      this.pendingAvatarPreviewUrl = null;
    },

    async updateProfile(profileData: Partial<BaseProfile>) {
      this.loading = true;
      try {
        const response = await fetchWrapper.patch(profileUrl, profileData);
        this.profile = response;
        this.clearPendingAvatarPreview();
        this.clearPendingAvatarStatus();
        localStorage.setItem('profile', JSON.stringify(response));
        return response;
      } catch (error: any) {
        this.errorCode = error?.code ?? 'profile_update_error';
        throw { ...error, code: this.errorCode };
      } finally {
        this.loading = false;
      }
    },

    async fetchPsychologistFilters() {
      this.loading = true;
      try {
        const url = `${import.meta.env.VITE_API_URL}/psychologist/filters`;
        const response = await fetchWrapper.get(url);
        this.psychologistFilters = response;
        return response;
      } catch (error: any) {
        this.errorCode = error?.code ?? 'psychologist_filters_fetch_error';
        throw { ...error, code: this.errorCode };
      } finally {
        this.loading = false;
      }
    },

    async updatePsychologistProfile(profileData: Partial<PsychologistProfile>) {
      this.loading = true;
      try {
        const response = await fetchWrapper.patch(psychologistProfileUrl, profileData);
        this.profile = response;
        this.clearPendingAvatarPreview();
        this.clearPendingAvatarStatus();
        localStorage.setItem('profile', JSON.stringify(response));
        return response;
      } catch (error: any) {
        this.errorCode = error?.code ?? 'profile_update_error';
        throw { ...error, code: this.errorCode };
      } finally {
        this.loading = false;
      }
    },

    async deleteProfile() {
      this.loading = true;
      try {
        await fetchWrapper.delete(profileUrl);
        this.profile = null;
        this.clearPendingAvatarPreview();
        this.clearPendingAvatarStatus();
        this.clearRole();
        const authStore = useAuthStore();
        authStore.logout();
      } catch (error: any) {
        this.errorCode = error?.code ?? 'profile_delete_error';
        throw { ...error, code: this.errorCode };
      } finally {
        this.loading = false;
      }
    },

    clearError() {
      this.errorCode = null;
    },

    clearRole() {
      this.role = null;
      localStorage.removeItem('role');
    },
  },
});
